import { pushDataLayer, type ZuvoEventName, type ZuvoEventProperties } from "@zuvo-ch/kit/events";
import { isValidGtmId } from "@zuvo-ch/kit/gtm";

/*
 * Google Tag Manager in the app: loaded only when the server hands out a container id (production
 * with GTM_ID set). The server records every standard event itself; the browser only mirrors the
 * conversions (sign_up, login, begin_checkout, purchase) to GTM. Never in /ops. No cookie banner (zuvo decision).
 */

type Host = { dataLayer?: unknown[] };

let ready: Promise<boolean> | null = null;

export function initAnalytics(): Promise<boolean> {
  // The privacy policy promises no GTM in the operator area.
  if (window.location.pathname.startsWith("/ops")) return Promise.resolve(false);
  ready ??= (async () => {
    const { gtmId } = (await fetch("/api/config")
      .then((r) => (r.ok ? r.json() : {}))
      .catch(() => ({}))) as { gtmId?: string | null };
    if (!gtmId || !isValidGtmId(gtmId)) return false;
    const host = window as Host;
    host.dataLayer ??= [];
    host.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
    document.head.appendChild(script);
    return true;
  })();
  return ready;
}

/** Mirrors a conversion to GTM. Does nothing without GTM. */
export async function track<N extends ZuvoEventName>(name: N, properties: ZuvoEventProperties[N]) {
  if (await initAnalytics()) pushDataLayer(name, properties);
}

/** The sign-in callback marks a new or returning sign-in with ?kf_event=; mirror it once and drop it. */
export function trackSignInFromUrl() {
  const url = new URL(window.location.href);
  const event = url.searchParams.get("kf_event");
  if (!event) return;
  url.searchParams.delete("kf_event");
  window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
  if (event === "sign_up") void track("user.signed_up", { method: "microsoft" });
  if (event === "login") void track("user.logged_in", { method: "microsoft" });
}
